import {
  applicableQuestionnaireField,
  customContentField,
  dateMeasurementTakenField,
  declinableHistoryField,
  diagnosisField,
  heightField,
  informationField,
  labCarrierNameField,
  labDrawDateField,
  medicalFacilityOrProviderField,
  piiDataTypeField,
  ratableHistoryField,
  referenceField,
  riskDetailField,
  riskField,
  riskGroupField,
  weightField,
} from './constants';
import { Field, LetterTypes, Sources } from './types';

const getOtafLetterFields = (source: Sources) => {
  switch (source) {
    case Sources.appInfoDisclosure:
      return [applicableQuestionnaireField];
    case Sources.aps:
      return [medicalFacilityOrProviderField, ratableHistoryField];
    case Sources.buildDisclosedOnApp:
      return [heightField, weightField];
    case Sources.buildInAps:
      return [
        heightField,
        weightField,
        medicalFacilityOrProviderField,
        dateMeasurementTakenField,
      ];
    case Sources.eti:
      return [applicableQuestionnaireField];
    case Sources.rxEti:
      return [applicableQuestionnaireField];
    case Sources.mibAppEti:
      return [applicableQuestionnaireField];
    case Sources.mibInsuranceLabs:
      return [labDrawDateField, labCarrierNameField];
    case Sources.mibAps:
      return [medicalFacilityOrProviderField, ratableHistoryField];
    case Sources.custom:
      return [customContentField];
    default:
      return [];
  }
};

const getDeclineLetterFields = (source: Sources) => {
  switch (source) {
    case Sources.aps:
      return [medicalFacilityOrProviderField, declinableHistoryField];
    case Sources.buildInAps:
      return [
        heightField,
        weightField,
        medicalFacilityOrProviderField,
        dateMeasurementTakenField,
      ];
    case Sources.eti:
      return [applicableQuestionnaireField];
    case Sources.mibAppEti:
      return [applicableQuestionnaireField];
    case Sources.mibAps:
      return [medicalFacilityOrProviderField, declinableHistoryField];
    case Sources.medClaimsAps:
      return [medicalFacilityOrProviderField];
    case Sources.mibInsuranceLabs:
      return [labDrawDateField, labCarrierNameField];
    case Sources.custom:
      return [customContentField];
    default:
      return [];
  }
};

const getWithdrawalLetterFields = (source: Sources) => {
  switch (source) {
    case Sources.lackOfAps:
      return [medicalFacilityOrProviderField];
    case Sources.lackOfInformation:
      return [informationField];
    case Sources.miscellaneous:
      return [informationField];
    case Sources.pii:
      return [piiDataTypeField];
    default:
      return [];
  }
};

const getOtafSamePriceLetterFields = (source: Sources) => {
  switch (source) {
    case Sources.appInfoDisclosure:
      return [riskGroupField, riskDetailField];
    case Sources.aps:
      return [referenceField, diagnosisField];
    case Sources.eti:
      return [riskField];
    case Sources.custom:
      return [customContentField];
    default:
      return [];
  }
};

const getReinstatementDeclineLetterFields = (source: Sources) => {
  switch (source) {
    case Sources.custom:
      return [customContentField];
    default:
      return [];
  }
};

export const getLetterFields = (
  letterType: LetterTypes,
  source: Sources,
): Field[] => {
  switch (letterType) {
    case LetterTypes.otaf:
      return getOtafLetterFields(source);
    case LetterTypes.decline:
      return getDeclineLetterFields(source);
    case LetterTypes.withdrawal:
      return getWithdrawalLetterFields(source);
    case LetterTypes.otafSamePrice:
      return getOtafSamePriceLetterFields(source);
    case LetterTypes.reinstatementDecline:
      return getReinstatementDeclineLetterFields(source);
    default:
      return [];
  }
};

export const getAvailableSources = (letterType: LetterTypes) => {
  switch (letterType) {
    case LetterTypes.otaf:
      return [
        Sources.appInfoDisclosure,
        Sources.aps,
        Sources.buildDisclosedOnApp,
        Sources.buildInAps,
        Sources.eti,
        Sources.financial,
        Sources.labPiQture,
        Sources.mibAppEti,
        Sources.mibAps,
        Sources.mibLabPiQture,
        Sources.rxEti,
        Sources.mibInsuranceLabs,
        Sources.mvr,
        Sources.rxOrMedClaimsMilliman,
        Sources.rxOrMedClaimsExamOne,
        Sources.trl,
        Sources.custom,
      ];
    case LetterTypes.decline:
      return [
        Sources.appInfoDisclosure,
        Sources.aps,
        Sources.buildDisclosedOnApp,
        Sources.buildInAps,
        Sources.criminalHistory,
        Sources.eti,
        Sources.labPiQture,
        Sources.mibAppEti,
        Sources.mibAps,
        Sources.mibLabPiQture,
        Sources.mibRxOrMedClaimsMilliman,
        Sources.medClaimsAps,
        Sources.mibInsuranceLabs,
        Sources.mvr,
        Sources.rxOrMedClaimsMilliman,
        Sources.rxOrMedClaimsExamOne,
        Sources.trl,
        Sources.custom,
      ];
    case LetterTypes.withdrawal:
      return [
        Sources.eti,
        Sources.lackOfAps,
        Sources.lackOfInformation,
        Sources.miscellaneous,
        Sources.pii,
        Sources.specialCharacter,
        Sources.unresolvedMib,
      ];
    case LetterTypes.otafSamePrice:
      return [
        Sources.appInfoDisclosure,
        Sources.aps,
        Sources.eti,
        Sources.rxOrMedClaimsMilliman,
        Sources.custom,
      ];
    case LetterTypes.reinstatementDecline:
      return [Sources.custom, Sources.medicalCondition];
    default:
      return [];
  }
};
